import { useFormik } from 'formik'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import useFormTracking from '../../../hooks/useFormTracking'
import { submitForm, submitFormWithFiles } from '../../../services/api/cms'
import { getCookieFromBrowser } from '../../../services/api/cookies'
import { FormsAPIProps, FormsProps } from '../../../services/api/types'
import {
  dynamicValidation,
  getFormInitValues,
  getURLParamsFromFormValues
} from '../../../services/utils'
import * as G from '../../../styles/global.styles'
import { Container, Section } from '../../../styles/grid.styles'

// Dynamic import due to SSR issue with react-select as per
// https://github.com/JedWatson/react-select/issues/3590
const StandardForm = dynamic(() => import('./StandardForm'), {
  ssr: false
})
const MemberEnquiryForm = dynamic(() => import('./MemberEnquiryForm'), {
  ssr: false
})

export const Forms = (props: FormsProps) => {
  const {
    slug,
    ctaButtons,
    fields,
    postRedirectPage,
    includeFieldsOnPost,
    pageType
  } = props
  const [responseMessage, setResponseMessage] = useState('')
  const [success, setSuccess] = useState<boolean | null>(null)
  let fileFieldText: Array<string> = []
  fields &&
    fields.length > 0 &&
    fields.map(field => {
      if (field.type === 'file') {
        fileFieldText.push(field.name)
      }
    })
  const hasFile = fileFieldText && fileFieldText.length > 0

  const { trackStart, trackProgress, trackError, trackSubmit } =
    useFormTracking(slug, props.reference_id)
  const trackableFieldNames =
    fields
      ?.filter(
        field => field.type !== 'hidden' && field.field_block !== 'recaptcha'
      )
      .map(field => field.name) || []
  const trackableFieldCount = trackableFieldNames.length

  const formik = useFormik({
    initialValues: getFormInitValues(props.fields),
    validationSchema: dynamicValidation(props.fields),
    onSubmit: async values => {
      if (!slug) {
        return
      }

      const utmSourceCookie = getCookieFromBrowser('utm_source')
      const utmCampaignCookie = getCookieFromBrowser('utm_campaign')
      if (utmSourceCookie && utmCampaignCookie) {
        values['utm_source'] = utmSourceCookie
        values['utm_campaign'] = utmCampaignCookie
      }

      if (slug.includes('join-now')) {
        values['gymJoinUrl'] = values.selectedGym?.url ?? ''
      }

      let fileData: any
      let fieldData: any
      if (hasFile) {
        fileData = Object.keys(values)
          .filter(key => fileFieldText.includes(key))
          .reduce((obj: any, key: string) => {
            obj[key] = values[key]
            return obj
          }, {})
        fieldData = Object.keys(values)
          .filter((key: string) => !fileFieldText.includes(key))
          .reduce((obj: any, key: string) => {
            obj[key] = values[key]
            return obj
          }, {})
      }

      let res = hasFile
        ? await submitFormWithFiles(null, slug, fieldData, fileData)
        : await submitForm(null, slug, values)
      if (res.ok) {
        setResponseMessage(res.success_message)
        setSuccess(true)
        trackSubmit()

        if (props.slug === '4weeksfree') {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'formSubmission',
            formId: props.reference_id,
            formName: props.slug,
            timestamp: new Date().toISOString()
          })
        }

        if (slug.includes('join-now')) {
          const gymUrl = values.selectedGym?.url ?? ''
          const selectedGym = values.selectedGym?.label ?? ''

          const urlParams = res.register_prefill_token
            ? `?token=${encodeURIComponent(
                res.register_prefill_token
              )}&gym=${encodeURIComponent(selectedGym)}#detail`
            : `?phoneNumber=${encodeURIComponent(
                values.phone || values.phoneNumber || values?.['phone-number']
              )}&email=${encodeURIComponent(
                values.email || values.emailAddress || values?.['email-address']
              )}&gym=${encodeURIComponent(selectedGym)}#detail`
          if (values.selectedGym.redirectEnabled) {
            setTimeout(() => {
              window.location.assign(
                gymUrl.includes('join.plus.fitness')
                  ? `${gymUrl}${urlParams}`
                  : gymUrl
              )
            }, 500)
          }
        }

        // Handle post redirect logic
        if (postRedirectPage) {
          const urlParams = getURLParamsFromFormValues(values)
          const url = includeFieldsOnPost
            ? `${postRedirectPage}?${urlParams}`
            : postRedirectPage

          window.location.href = url
        }
      } else {
        setResponseMessage(res.error_message)
        setSuccess(false)
        trackError()
        throw responseMessage
      }
    }
  })

  useEffect(() => {
    const filledFieldCount = trackableFieldNames.filter(name => {
      const value = formik.values[name]
      return Array.isArray(value) ? value.length > 0 : Boolean(value)
    }).length
    if (filledFieldCount === 0) {
      return
    }
    trackStart()
    trackProgress(filledFieldCount, trackableFieldCount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values])

  useEffect(() => {
    if (formik.submitCount > 0 && !formik.isValid) {
      trackError(Object.keys(formik.errors))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount])

  if (success) {
    return (
      <Section>
        <Container style={{ textAlign: 'center' }}>
          <G.Text color='white' fontWeight='bold'>
            {responseMessage || 'Your enquiry was successful.'}
          </G.Text>
          <G.Space />
          {/* Hide cta button on form success page if form is from FranchisePage */}
          {pageType !== 'FranchisePage' &&
            ctaButtons?.map(cta => (
              <G.BlueButtonLink href={cta.href}>{cta.label}</G.BlueButtonLink>
            ))}
        </Container>
      </Section>
    )
  } else {
    if (slug.includes('memberships-enquiry')) {
      return <MemberEnquiryForm formik={formik} {...props} />
    } else if (slug.includes('performance-enquire')) {
      return <MemberEnquiryForm formik={formik} formColums={2} {...props} />
    } else return <StandardForm formik={formik} {...props} />
  }
}

Forms.ApiBlockToProps = (data: FormsAPIProps): FormsProps => {
  const apiData = data.value
  const {
    form,
    slug,
    form_action,
    title,
    preamble,
    pre_form_text,
    reference_id,
    submit_button_text,
    success_message,
    error_message,
    post_redirect_page,
    include_fields_on_post,
    fields,
    gym_id,
    gym_title,
    pop_up_form,
    button_colour
  } = apiData

  return {
    slug,
    title,
    fields: form.fields
      ? form.fields?.filter(
        (it: { field_block?: string }) => it?.field_block !== 'lead_mapping'
      )
      : fields?.filter(it => it.field_block !== 'lead_mapping'),
    preamble,
    reference_id,
    id: form.form ? form.form : form,
    formAction: form_action,
    preFormText: pre_form_text,
    submitText: submit_button_text,
    successMessage: success_message,
    errorMessage: error_message,
    postRedirectPage: post_redirect_page,
    includeFieldsOnPost: include_fields_on_post,
    ctaButtons: [
      {
        color: 'blue-deep',
        href: '/',
        label: 'Back To Homepage'
      }
    ],
    gymId: gym_id,
    gymTitle: gym_title,
    pop_up_form: form?.pop_up_form || pop_up_form,
    buttonColor: button_colour
  }
}

export default Forms
